import localforage from 'localforage'
import ReconnectingWebSocket from 'reconnecting-websocket'
import * as moment from 'moment'

class FollowAPIError extends Error {
	constructor (opts) {
		const superOpts = typeof opts === 'string'
			? opts
			: opts.message;
		super (superOpts);
		this.name = "FollowAPI Error";
		this.message = opts.message;
		this.code = opts.code;
		this.detail = opts.detail;
		this.unauthorised = opts.unauthorised;

		// Work around for stupid Babel bug
		// https://github.com/babel/babel/issues/4485
		this.constructor = FollowAPIError
		this.__proto__   = FollowAPIError.prototype
	}
}
 
class FollowAPI {
	
	constructor (apiUrl) {
		this.url = apiUrl || (document.location.origin + "/api/v0");
		this.ws = null;
		this.subscribers = [];
	}
	
	async handleErrors (response, {broadcast, expectErrors} = {}) {
		let json;
		if (response) {
			try {
				json = await response.json();
			} catch (err) {
				// Do not complain if body is empty.
			}
			if (!response.ok) {
				let error;
				switch (response.status) {
					case 400:
						error = new FollowAPIError({
							code: "missing_required_data",
							message: "Missing required data",
							detail: { response }
						});
						break;
					case 401:
						error = new FollowAPIError({
							code: "authorisation_required",
							message: "Authorisation required",
							unauthorised: true,
							detail: { response }
						});
						break;
					case 404:
						error = new FollowAPIError({
							code: "not_found",
							message: "Not found",
							detail: { response }
						});
						break;
					case 413:
						error = new FollowAPIError({
							code: "payload_too_large",
							message: "Payload too large",
							detail: { response }
						});
						break;
					case 500:
						error = new FollowAPIError({
							code: "server_error",
							message: "Server error",
							detail: { response }
						});
						break;
					case 502:
						error = new FollowAPIError({
							code: "bad_gateway",
							message: "API not available",
							detail: { response }
						});
						break;
				}
				
				if (broadcast !== false && this.eventTarget) {
					const event = new CustomEvent('follow-error', {detail: error});
					if (Array.isArray(expectErrors)) {
						if (!expectErrors.includes(response.status) && !expectErrors.includes(error.code)) {
							this.eventTarget.dispatchEvent(event);
						}
					} else if (expectErrors !== true) {
						this.eventTarget.dispatchEvent(event);
					}
				}
				
				if (Array.isArray(expectErrors)) {
					if (!expectErrors.includes(response.status) && !expectErrors.includes(error.code)) {
						throw error;
					}
				} else if (expectErrors !== true) {
					throw error;
				}
			}
		}
		return json;
	}
	
	async prepareRequest (endpoint, options = {}) {
		const jwt = await localforage.getItem('jwt');
		const url = this.url + endpoint;
		const init = options;
		if (jwt) {
			init.headers = Object.assign({}, init.headers, {
				"Authorization": "Bearer " + jwt
			});
		}

		return {url, init, jwt};
	}
	
	async fetch (endpoint, options = {}) {
		const {url, init} = await this.prepareRequest(endpoint, options);
		const res = await fetch(url, init);
		const newJwt = res.headers.get("x-jwt-token");
		if (newJwt) {
			localforage.setItem("jwt", newJwt); // Do not await
		}
		return res;
	}
	
	async get (endpoint, opts = {}) {
		const res = await this.fetch(endpoint);
		
		return this.handleErrors(res, opts.errors);
	}
	
	async post (endpoint, payload, opts = {}) {
		const res = await this.fetch(endpoint, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: payload instanceof Blob
				? payload
				: JSON.stringify(payload)
		});
		
		return this.handleErrors(res, opts.errors);
	}
	
	async remove (endpoint) {
		const res = await this.fetch(endpoint, { method: "DELETE" });
		
		return this.handleErrors(res);
	}
	
	async login (credentials, opts = {}) {
		await localforage.removeItem('jwt');
		
		try {
			const json = await this.post("/user/login", credentials, opts);
			console.log("LOGIN", json);
			if (json && json.jwt) {
				await localforage.setItem('jwt', json.jwt);
				return json.jwt;
			} else if (json && json.error) {
				throw new FollowAPIError({code: json.error, unauthorised: true});
			} else {
				throw new FollowAPIError({code: "invalid_response"});
			}
		} catch (error) {
			if (error.code == "authorisation_required") {
				throw new FollowAPIError({
					code: "bad_login",
					message: "Bad login",
					unauthorised: true,
					detail: error.detail
				});
			} else {
				throw error;
			}
		}
	}
	
	async logout () {
		await localforage.removeItem('jwt');
		this.stopWebsocket();
	}
	
	async checkHandleExists (handle) {
		const opts = { errors: { expectErrors: [ 404 ] } };
		return this.get("/user/handle/"+handle, opts);
	}
	
	async createUser (credentials) {
		const res = await this.post("/user", credentials);
		console.log("CUR", res);
		return res;
	}
	
	async removeUser () {
		return this.remove("/user");
	}
	
	async createToken (data) {
		return this.post("/token", data);
	}
	
	async updateToken (token_id, data) {
		return this.post("/token/"+token_id, data);
	}
	
	async listTokens (opts) {
		let url = "/token";
		if (opts && (opts.limit || opts.offset)) {
			url += "?" + Object.keys(opts).map(key => {
				return encodeURIComponent(key) + "=" + encodeURIComponent(opts[key])
			}).join("&")
		}
		
		return await this.get(url) || [];
	}
	
	async listTokenShares (token_id, params = {}) {
		let url = "/token/"+encodeURIComponent(token_id)+"/share";
		if (params && (params.limit || params.offset)) {
			url += "?" + Object.keys(params).map(key => {
				return encodeURIComponent(key) + "=" + encodeURIComponent(params[key])
			}).join("&")
		}
		
		return await this.get(url) || [];
	}
	
	async getToken (token_id) {
		return this.get("/token/"+encodeURIComponent(token_id));
	}
	
	async removeToken (token_id) {
		return this.remove("/token/"+encodeURIComponent(token_id));
	}
	
	async shareToken (token_id, params = {}) {
		const recipient_id = encodeURIComponent(params.public ? "00000000" : params.recipient_id);
		const permissions = encodeURIComponent(
			params.public
				? params.public_visible
					? "rs"
					: "r"
				: [...(params.permissions||"")].filter(p => /[DUcruds]/.test(p)).join("")
		);
		const expiry = params.expiry
			? moment(params.expiry).valueOf()
			: "";
		const url = `/token/${encodeURIComponent(token_id)}/share/${recipient_id}/${permissions}/${expiry}`;
		return this.post(url, {});
	}
	
	async getFixes (token_id, opts) {
		let url = "/token/"+encodeURIComponent(token_id)+"/fixes";
		if (opts && (opts.limit || opts.offset)) {
			url += "?" + Object.keys(opts).map(key => {
				return encodeURIComponent(key) + "=" + encodeURIComponent(opts[key])
			}).join("&")
		}

		return await this.get(url) || []
	}
	
	async saveFix (token_id, fixData) {
		return this.post("/token/"+token_id+"/save", fixData);
	}
	
	async uploadFixes (token_id, data, opts = {}) {
		const url = "/token/"+encodeURIComponent(token_id)+"/fixes.json";
		return await this.post(url, data);
	}
	
	async downloadFixes (token_id, opts = {}) {
		const url = "/token/"+encodeURIComponent(token_id)+"/fixes.json";
		const res = await this.fetch(url);
		const blob = await res.blob();
		let fileName = (opts.name ? opts.name+"-" : "") + token_id+"-fixes.geojson";
		
		let domElement = document.createElement("a");
		domElement.download = fileName;
		domElement.style.display = "none";
		domElement.href = window.URL.createObjectURL(blob);
		domElement.onclick = (event) => {
			document.body.removeChild(event.target);
		};
		document.body.appendChild(domElement);
		domElement.click();
		return blob;
	}
	
	async startWebsocket () {
		
		if (this.ws) {
			console.log("Websocket already started. Reconnecting …");
			this.ws.reconnect();
			return;
		}
		
		const jwt = await localforage.getItem('jwt');
		const wsurl = location.origin.toString().replace(/^http/, "ws") + "/api/v0/ws";
		
		console.log("WS ADD", wsurl);
		const ws = new ReconnectingWebSocket(wsurl);
		
		const openHandler = async (event) => {
			console.log("WS OPEN");
			ws.send(JSON.stringify({
				type: "authentication",
				jwt
			}));
			
			// Renew subscriptions
			const subscribers = this.subscribers;
			this.subscribers = [];
			for (const {channel, callback} of subscribers) {
				await this.subscribe(channel, callback);
			};
		};
		
		const messageHandler = (event) => {
			console.log("WS RX", event);
			if (event.data) {
				const message = JSON.parse(event.data);
				if (message) {
					switch (message.type) {
						case "error":
							switch (message.code) {
								case "unauthorised":
									console.warn("Websocket authorisation failed");
									this.stopWebsocket();
									break;
								default:
									console.warn("Websocket error", message.code);
							}
							break;
						case "authentication":
							console.log("Received new JWT", message.jwt);
							localforage.setItem("jwt", jwt);
							break;
						case "subscription":
							console.log("Received subscription data", message);
							this.subscribers
							.filter(subs => subs.channel == message.channel)
							.forEach(subs => subs.callback(message.payload));
							break;
						case "status":
							switch (message.status) {
								case "connection_close":
									console.log("Closing connection on server's request");
									this.stopWebsocket();
									break;
								default:
									console.warn("Unrecognised status message", message);
							}
							break;
						default:
							console.warn("Websocket unrecognised message type", message.type);
					}
					
					if (this.eventTarget) {
						const event = new CustomEvent('follow-message', {detail: message});
						this.eventTarget.dispatchEvent(event);
					}
				}
			}
		};
		
		const closeHandler = (event) => {
// 			console.log("WS CLOSED", event);
		};
		
		ws.addEventListener('open', openHandler);
		ws.addEventListener('message', messageHandler);
		ws.addEventListener('close', closeHandler);
		
		this.ws = ws;
	}
	
	async stopWebsocket () {
		if (this.ws) {
			this.ws.close();
			delete this.ws;
		}
	}
	
	async subscribe (channel, callback) {
		if (!this.ws) {
			await this.startWebsocket();
		}
		this.ws.send(JSON.stringify({
			type: "subscription",
			channel
		}));
		
		// Make sure that we're not already subscribed,
		// otherwise the same callback would get called
		// multiple times.
		if (!this.subscribers.find(s => s.channel == channel && s.callback == callback)) {
			this.subscribers.push({channel, callback});
		}
	}
	
	async unsubscribe (channel, callback) {
		if (this.ws) { // If there is no websocket, there are no subscriptions
			this.ws.send(JSON.stringify({
				type: "unsubscription",
				channel,
			}));
			
			this.subscribers =
				this.subscribers
				.filter(subs => !(subs.channel == channel &&
					(callback === undefined || subs.callback === callback))
				);
		}
	}

	/**
	 * Return the 95% circular error probable (CEP) value
	 * 
	 * This routine tries to obtain or derive from the fix data
	 * a measure of the positioning uncertainty.
	 * 
	 * It is assumed that the following fix fields, if present,
	 * contain a 95% CEP (aka R95), which is then returned unchanged:
	 * 
	 * - accuracy
	 * - metadata.accuracy
	 * - metadata.accuracy_h
	 * 
	 * If an HDOP value is present, it is converted to R95 using
	 * either pseudoRangeSD, which if present should be a one-sigma
	 * estimate of pseudorange standard deviation, or the UERE
	 * multiplier, representing the user equivalent range error.
	 * 
	 * If no data could be found in any of the above fields,
	 * returns undefined.
	 * 
	 */
	// FIXME should be a static method, but Webpack barfs on it ☹
	/*static*/ fixAccuracy2D (fix, uere = 5, pseudoRangeSD) {
		
		if (!fix || !(fix instanceof Object)) {
			return;
		}
		
		if (fix.accuracy) {
			return fix.accuracy;
		}
		
		if (fix.metadata) {
			if (fix.metadata.accuracy) {
				return fix.metadata.accuracy;
			}
			
			if (fix.metadata.accuracy_h) {
				return fix.metadata.accuracy_h;
			}
			
			if (fix.metadata.hdop) {
				if (pseudoRangeSD) {
					const hdop_sq = Math.pow(fix.metadata.hdop, 2);
					const sr_sq = Math.pow(pseudoRangeSD, 2);
					// In NRPL ($$HDOP = hdop, $$SR = pseudoRangeSD)
					// .589 2 $$HDOP² $$SR² * 2 / √ * *
					return 0.589 * (2 * Math.sqrt((hdop_sq * sr_sq)/2)) * 2.1;
				} else if (uere) {
					return fix.metadata.hdop * uere;
				}
			}
		}
	}
	
};

export { FollowAPIError, FollowAPI };
